/**
 * V2 Droplink API Service
 * 기존 DroplinkAPI.ts를 V2 스타일로 재구현
 */

import type { Droplink } from 'oscar-typedef'
import { apiInterceptor } from '@/networks/api/interceptor'

const BASE_PATH = '/droplink'

/**
 * V2 API 응답 형식
 */
interface V2ApiResponse<T> {
  success: boolean
  message: string
  data: T
  timestamp: string
}

/**
 * V2 응답에서 data 추출
 */
async function extractData<T>(response: Response): Promise<T> {
  const json: V2ApiResponse<T> = await response.json()
  if (!json.success) {
    throw new Error(json.message || 'API request failed')
  }
  return json.data
}

/**
 * 캠페인 목록 조회
 */
export async function getCampaign(
  queries: Droplink.GetDroplinkCampaignList.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignList.Response.Body> {
  const params = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value))
    }
  })

  const queryString = params.toString()
  const url = `${BASE_PATH}/campaign${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to get campaign list: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 랜딩 페이지 정보 조회
 */
export async function getCampaignLanding(
  params: Droplink.GetDroplinkCampaignLanding.Request.Params,
  queries: Droplink.GetDroplinkCampaignLanding.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignLanding.Response.Body> {
  const urlParams = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value))
    }
  })

  const queryString = urlParams.toString()
  const url = `${BASE_PATH}/campaign/${params.campaignId}/landing${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to get campaign landing: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 상세 정보 조회
 */
export async function getCampaignDetail(
  params: Droplink.GetDroplinkCampaignDetail.Request.Params,
  queries: Droplink.GetDroplinkCampaignDetail.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignDetail.Response.Body> {
  const urlParams = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value))
    }
  })

  const queryString = urlParams.toString()
  const url = `${BASE_PATH}/campaign/${params.campaignId}/detail${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to get campaign detail: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 클레임 정보 조회
 */
export async function getClaim(
  params: Droplink.GetDroplinkCampaignClaim.Request.Params,
  queries: Droplink.GetDroplinkCampaignClaim.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignClaim.Response.Body> {
  const urlParams = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value))
    }
  })

  const queryString = urlParams.toString()
  const url = `${BASE_PATH}/campaign/${params.campaignId}/claim${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to get claim: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 참여 요청
 */
export async function requestCampaign(
  params: Droplink.RequestDroplinkCampaign.Request.Params,
  body: Droplink.RequestDroplinkCampaign.Request.Body,
): Promise<Droplink.RequestDroplinkCampaign.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/request`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to request campaign: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 퀘스트 완료 기록 추가
 */
export async function addCampaignQuestHistory(
  params: Droplink.CompleteDroplinkQuest.Request.Params,
  body: Droplink.CompleteDroplinkQuest.Request.Body,
): Promise<Droplink.CompleteDroplinkQuest.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/quest`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to add quest history: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 클레임 요청
 */
export async function requestClaim(
  params: Droplink.ClaimDroplinkCampaign.Request.Params,
  body: Droplink.ClaimDroplinkCampaign.Request.Body,
): Promise<Droplink.ClaimDroplinkCampaign.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/claim`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to request claim: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 레퍼럴 코드 설정
 */
export async function referralCampaign(
  params: Droplink.SetDroplinkCampaignReferralCode.Request.Params,
  body: Droplink.SetDroplinkCampaignReferralCode.Request.Body,
): Promise<Droplink.SetDroplinkCampaignReferralCode.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/referral`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to set referral: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 참여 요청 이메일 발송
 */
export async function requestEmailCampaign(
  params: Droplink.SendRequestEmailDroplinkCampaign.Request.Params,
  body: Droplink.SendRequestEmailDroplinkCampaign.Request.Body,
): Promise<Droplink.SendRequestEmailDroplinkCampaign.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/request/email`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to send request email: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 참여 완료 이메일 발송
 */
export async function requestCompleteEmailCampaign(
  params: Droplink.SendRequestCompleteEmailDroplinkCampaign.Request.Params,
  body: Droplink.SendRequestCompleteEmailDroplinkCampaign.Request.Body,
): Promise<Droplink.SendRequestCompleteEmailDroplinkCampaign.Response.Body> {
  const url = `${BASE_PATH}/campaign/${params.campaignId}/request-complete/${params.requestId}/email`

  const response = await apiInterceptor.request(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to send complete email: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 캠페인 레퍼럴 코드 조회
 */
export async function getReferralCodeCampaign(
  params: Droplink.GetDroplinkCampaignRequest.Request.Params,
  queries: Droplink.GetDroplinkCampaignRequest.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignRequest.Response.Body> {
  const urlParams = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value))
    }
  })

  const queryString = urlParams.toString()
  const url = `${BASE_PATH}/campaign/${params.campaignId}/request${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to get referral code: ${response.status}`)
  }

  return extractData(response)
}

/**
 * 이메일로 캠페인 참여 여부 확인
 */
export async function checkEmailJoinCampaign(
  params: Droplink.GetDroplinkCampaignJoin.Request.Params,
  queries: Droplink.GetDroplinkCampaignJoin.Request.Queries,
): Promise<Droplink.GetDroplinkCampaignJoin.Response.Body> {
  const urlParams = new URLSearchParams()
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value))
    }
  })

  const queryString = urlParams.toString()
  const url = `${BASE_PATH}/campaign/${params.campaignId}/join${queryString ? `?${queryString}` : ''}`

  const response = await apiInterceptor.request(url, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Failed to check email join: ${response.status}`)
  }

  return extractData(response)
}

/**
 * Droplink API Service 객체
 * WepinBackendAPI에서 사용
 */
export const droplinkApiService = {
  getCampaign,
  getCampaignLanding,
  getCampaignDetail,
  getClaim,
  requestCampaign,
  addCampaignQuestHistory,
  requestClaim,
  referralCampaign,
  requestEmailCampaign,
  requestCompleteEmailCampaign,
  getReferralCodeCampaign,
  checkEmailJoinCampaign,
}

export default droplinkApiService
